import { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, RefreshCw, LogOut, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/api/client';
import { useAuthStore, parseJwtExpiration } from '@/store/useAuthStore';

// Session timeout configuration
const WARN_BEFORE_MINUTES = 5; // Show warning 5 minutes before expiry
const MIN_SESSION_MINUTES = 30; // Minimum session length to show badge
const ACTIVITY_THROTTLE_MS = 60000; // Only track activity once per minute
const ACTIVE_THRESHOLD_MS = 5 * 60 * 1000; // User considered "active" if activity within 5 minutes

export function SessionTimeout() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tokenExpiresAt, lastActivity, setTokenExpiration, updateLastActivity, clear: clearAuth } = useAuthStore();
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const autoRefreshAttemptedRef = useRef(false);

  // Initialize token expiration from stored token
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token && !tokenExpiresAt) {
      const expiresAt = parseJwtExpiration(token);
      if (expiresAt) {
        setTokenExpiration(expiresAt);
      }
    }
  }, [tokenExpiresAt, setTokenExpiration]);

  // Reset auto-refresh flag when token changes (i.e. after a successful refresh)
  useEffect(() => {
    autoRefreshAttemptedRef.current = false;
  }, [tokenExpiresAt]);

  // Track user activity
  const handleActivity = useCallback(() => {
    const now = Date.now();
    // Throttle activity updates
    if (!lastActivity || now - lastActivity > ACTIVITY_THROTTLE_MS) {
      updateLastActivity();
    }
  }, [lastActivity, updateLastActivity]);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [handleActivity]);

  // Refresh session by getting a new JWT token
  const refreshSession = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const response = await api.post<{ token: string }>('/auth/refresh');
      if (response.token) {
        api.setToken(response.token);

        const newExpiration = parseJwtExpiration(response.token);
        if (newExpiration) {
          setTokenExpiration(newExpiration);
        }

        updateLastActivity();
        setShowModal(false);
      }
    } catch {
      handleLogout();
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, setTokenExpiration, updateLastActivity]);

  const handleLogout = useCallback(() => {
    api.logout();
    clearAuth();
    navigate('/login');
  }, [clearAuth, navigate]);

  // Update countdown timer
  useEffect(() => {
    if (!tokenExpiresAt) {
      setTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, tokenExpiresAt - now);
      setTimeRemaining(remaining);

      const warningThreshold = WARN_BEFORE_MINUTES * 60 * 1000;
      const isInWarningZone = remaining > 0 && remaining < warningThreshold;

      // Show badge for 5-30 min range
      setShowWarning(remaining > 0 && remaining < MIN_SESSION_MINUTES * 60 * 1000);

      if (isInWarningZone) {
        const isUserActive = lastActivity && (now - lastActivity < ACTIVE_THRESHOLD_MS);

        if (isUserActive && !autoRefreshAttemptedRef.current) {
          // User is active - auto-refresh in background (no popup needed)
          autoRefreshAttemptedRef.current = true;
          refreshSession();
        } else if (!isUserActive) {
          // User is inactive - show modal
          setShowModal(true);
        }
      } else {
        setShowModal(false);
      }

      // Token expired: try to refresh first, only logout if refresh fails
      if (remaining <= 0) {
        if (!autoRefreshAttemptedRef.current) {
          autoRefreshAttemptedRef.current = true;
          refreshSession();
        } else {
          handleLogout();
        }
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [tokenExpiresAt, lastActivity, refreshSession, handleLogout]);

  // Format time remaining
  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    }

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Don't show anything if no expiration or more than 30 minutes remaining
  if (!timeRemaining || timeRemaining > MIN_SESSION_MINUTES * 60 * 1000) {
    return null;
  }

  const isUrgent = timeRemaining < WARN_BEFORE_MINUTES * 60 * 1000;

  return (
    <>
      {/* Header badge (5-30 min range, or urgent) */}
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
        isUrgent
          ? 'bg-red-100 text-red-700 border border-red-200'
          : showWarning
            ? 'bg-orange-100 text-orange-700 border border-orange-200'
            : 'bg-slate-100 text-slate-600'
      }`}>
        <Clock size={16} className={isUrgent ? 'text-red-500' : showWarning ? 'text-orange-500' : 'text-slate-400'} />
        <span className="font-mono tabular-nums">
          {formatTime(timeRemaining)}
        </span>
        {showWarning && (
          <button
            onClick={refreshSession}
            disabled={isRefreshing}
            className={`p-1 rounded transition-colors ${
              isUrgent ? 'hover:bg-red-200' : 'hover:bg-orange-200'
            }`}
            title={t('session.expiryWarning.continue')}
          >
            <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
          </button>
        )}
      </div>

      {/* Modal dialog - shown when < 5 min and user inactive */}
      {showModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Dialog */}
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 animate-in fade-in zoom-in duration-200">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <AlertTriangle size={32} className="text-orange-500" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-center text-gray-900 dark:text-gray-100 mb-2">
              {t('session.expiryWarning.title')}
            </h2>

            {/* Countdown */}
            <div className="flex justify-center my-4">
              <div className={`text-4xl font-mono font-bold tabular-nums px-6 py-3 rounded-xl ${
                timeRemaining < 60000
                  ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                  : 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
              }`}>
                {formatTime(timeRemaining)}
              </div>
            </div>

            {/* Message */}
            <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
              {t('session.expiryWarning.message', { time: formatTime(timeRemaining) })}
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleLogout}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
              >
                <LogOut size={18} />
                {t('session.expiryWarning.logout')}
              </button>
              <button
                onClick={refreshSession}
                disabled={isRefreshing}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
              >
                <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
                {t('session.expiryWarning.continue')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
